import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { getSwipeStats, getMatches, getDashboardChat, sendDashboardChat } from '../services/api';
import { 
  Send, MapPin, Heart, Settings, Home, Eye, X, Sparkles, 
  TrendingUp, Star, Zap, MessageSquare, Loader2, ChevronRight,
  User, Building2, ThumbsDown, Flame, Crown, ArrowRight, Lock
} from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const chatEndRef = useRef(null);
  
  const [stats, setStats] = useState({ totalSwipes: 0, likes: 0, passes: 0 });
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [activeTab, setActiveTab] = useState('matches');
  const [navigatingToPremium, setNavigatingToPremium] = useState(false);

  // Handle navigation to premium with loading animation
  const handleNavigateToPremium = async () => {
    setNavigatingToPremium(true);
    // Simulate realistic loading time
    await new Promise(resolve => setTimeout(resolve, 1500));
    navigate('/premium');
  };

  const isVerhuurder = user?.userType === 'verhuurder';
  // FREE FOR ALL - Everyone has full access
  const hasPremium = true;

  // Translation helper
  const t = {
    dashboard: language === 'nl' ? 'Dashboard' : 'Dashboard',
    yourStats: language === 'nl' ? 'Jouw Statistieken' : 'Your Statistics',
    swipes: language === 'nl' ? 'Swipes' : 'Swipes',
    likes: language === 'nl' ? 'Likes' : 'Likes',
    passes: language === 'nl' ? 'Passes' : 'Passes',
    matches: language === 'nl' ? 'Matches' : 'Matches',
    noMatches: language === 'nl' ? 'Nog geen matches' : 'No matches yet',
    noMatchesDesc: language === 'nl' ? 'Begin met swipen om je eerste match te krijgen!' : 'Start swiping to get your first match!',
    startSwiping: language === 'nl' ? 'Start swipen' : 'Start swiping',
    viewProperty: language === 'nl' ? 'Bekijk woning' : 'View property',
    chat: language === 'nl' ? 'Chat' : 'Chat',
    typeMessage: language === 'nl' ? 'Typ je bericht...' : 'Type your message...',
    matchBadge: language === 'nl' ? 'Match! Je hebt interesse getoond' : 'Match! You showed interest',
    perMonth: language === 'nl' ? '/mnd' : '/mo',
    total: language === 'nl' ? 'totaal' : 'total',
    interested: language === 'nl' ? 'geïnteresseerd' : 'interested',
    skipped: language === 'nl' ? 'overgeslagen' : 'skipped',
    successRate: language === 'nl' ? 'Match rate' : 'Match rate',
    keepSwiping: language === 'nl' ? 'Blijf swipen!' : 'Keep swiping!',
    goPremium: language === 'nl' ? 'Ga Premium' : 'Go Premium',
    unlimitedSwipes: language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swipes',
    ago: language === 'nl' ? 'geleden' : 'ago',
    now: language === 'nl' ? 'Nu' : 'Now',
    profile: language === 'nl' ? 'Profiel' : 'Profile',
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isVerhuurder) {
      navigate('/verhuurder/dashboard');
      return;
    }
    loadDashboardData();
  }, [isAuthenticated, authLoading, navigate, isVerhuurder]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, matchesRes] = await Promise.all([
        getSwipeStats().catch(() => ({ totalSwipes: 0, likes: 0, passes: 0 })),
        getMatches().catch(() => ({ matches: [] }))
      ]);
      setStats(statsRes);
      setMatches(matchesRes.matches || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (match) => {
    setSelectedMatch(match);
    setLoadingChat(true);
    setChatMessages([]);
    
    try {
      const listingId = match.listing?.id;
      const otherUserId = match.landlord?.id;
      
      if (listingId && otherUserId) {
        const response = await getDashboardChat(listingId, otherUserId);
        // API returns { messages: [...] }, extract the array
        setChatMessages(response?.messages || []);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      setChatMessages([]);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedMatch) return;
    
    setSendingMessage(true);
    try {
      const listingId = selectedMatch.listing?.id;
      const receiverId = selectedMatch.landlord?.id;
      
      if (!listingId || !receiverId) {
        toast({ title: language === 'nl' ? 'Kan bericht niet versturen' : 'Cannot send message', variant: 'destructive' });
        return;
      }
      
      const res = await sendDashboardChat(selectedMatch.id, listingId, receiverId, newMessage);
      if (res.success && res.message) {
        setChatMessages(prev => [...prev, res.message]);
        setNewMessage('');
      }
    } catch (error) {
      toast({ title: language === 'nl' ? 'Fout bij versturen' : 'Error sending', variant: 'destructive' });
    } finally {
      setSendingMessage(false);
    }
  };

  const formatMatchTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffHours < 1) return t.now;
      if (diffHours < 24) return `${diffHours}h ${t.ago}`;
      if (diffDays < 7) return `${diffDays}d ${t.ago}`;
      return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US');
    } catch { return ''; }
  };

  const matchRate = stats.totalSwipes > 0 ? Math.round((stats.likes / stats.totalSwipes) * 100) : 0;

  // Loading overlay for premium navigation
  if (navigatingToPremium) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="mb-6 animate-pulse">
            <SwipeMatchLogo size="large" variant="white" className="justify-center" />
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
            </div>
            <p className="text-white/80 text-sm font-medium">
              {language === 'nl' ? 'Premium opties laden...' : 'Loading premium options...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center">
        <div className="text-center">
          <SwipeMatchLogo size="large" variant="white" className="justify-center mb-4" />
          <div className="flex items-center gap-2 text-white/60 justify-center">
            <div className="w-2 h-2 bg-[#2E7D32] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </div>
      </div>
    );
  }

  // Chat Overlay - Full screen on mobile
  if (selectedMatch) {
    return (
      <div className="fixed inset-0 z-50 bg-[#F7F7F7] flex flex-col" data-testid="chat-overlay">
        {/* Chat Header - Compact */}
        <div className="bg-white border-b border-[#E5E5E5] px-3 py-2 sm:px-4 sm:py-3 safe-area-top">
          <div className="flex items-center gap-2 sm:gap-3 max-w-2xl mx-auto">
            <button 
              onClick={() => setSelectedMatch(null)}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#F7F7F7] flex items-center justify-center hover:bg-[#E5E5E5] transition-colors flex-shrink-0"
              data-testid="close-chat-btn"
            >
              <X className="w-5 h-5 text-[#2A2A2A]" />
            </button>
            
            {/* Clickable listing info - navigates to listing detail */}
            <div 
              className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 cursor-pointer hover:bg-[#F7F7F7] rounded-xl p-1 -m-1 transition-colors"
              onClick={() => navigate(`/woning/${selectedMatch.listing?.id}`)}
              data-testid="chat-listing-link"
            >
              {selectedMatch.listing?.image ? (
                <img src={selectedMatch.listing.image} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#E5E5E5] flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-[#9E9E9E]" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-bold text-[#2A2A2A] truncate text-sm sm:text-base hover:text-[#2E7D32] transition-colors">{selectedMatch.listing?.title}</div>
                <div className="text-xs sm:text-sm text-[#2E7D32] font-medium">€{selectedMatch.listing?.price}/mnd {selectedMatch.listing?.utilitiesIncluded === true ? 'incl.' : selectedMatch.listing?.utilitiesIncluded === false ? 'excl.' : ''}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#9E9E9E] flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="max-w-2xl mx-auto space-y-3">
            {/* Match badge - smaller on mobile */}
            <div className="flex justify-center mb-4">
              <div className="bg-[#2E7D32]/10 text-[#2E7D32] px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5">
                <Heart className="w-3.5 h-3.5 fill-[#2E7D32]" />
                {t.matchBadge}
              </div>
            </div>

            {loadingChat ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#2E7D32] animate-spin" />
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="text-center py-6 text-[#9E9E9E]">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">{language === 'nl' ? 'Start het gesprek!' : 'Start the conversation!'}</p>
              </div>
            ) : (
              chatMessages.map((msg, i) => {
                const isMe = msg.senderId === user?.id || msg.sender === 'me';
                return (
                  <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-3 py-2 sm:px-4 sm:py-3 ${
                      isMe 
                        ? 'bg-[#2E7D32] text-white rounded-br-sm' 
                        : 'bg-white border border-[#E5E5E5] text-[#2A2A2A] rounded-bl-sm'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.content || msg.text}</p>
                      <p className={`text-[10px] sm:text-xs mt-1 ${isMe ? 'text-white/60' : 'text-[#9E9E9E]'}`}>
                        {msg.time || (msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' }) : '')}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={chatEndRef} />
          </div>
        </div>

        {/* Message Input - Check if match allows chat */}
        <div className="bg-white border-t border-[#E5E5E5] px-3 sm:px-4 py-2 sm:py-3 safe-area-bottom">
          <div className="max-w-2xl mx-auto">
            {selectedMatch?.canChat !== false ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t.typeMessage}
                  className="flex-1 h-10 sm:h-12 rounded-full border-[#E5E5E5] px-4 text-sm text-[#2A2A2A] bg-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={sendingMessage}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="w-10 h-10 sm:w-12 sm:h-12 bg-[#2E7D32] rounded-full flex items-center justify-center text-white hover:bg-[#256B2B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </div>
            ) : (
              /* Upgrade CTA for pending/free likes */
              <div className="bg-gradient-to-r from-[#2E7D32]/10 to-[#1F3A5F]/10 rounded-xl p-4 border border-dashed border-[#2E7D32]/40">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#2A2A2A] text-sm">
                      {language === 'nl' ? 'Upgrade om te chatten' : 'Upgrade to chat'}
                    </p>
                    <p className="text-xs text-[#9E9E9E]">
                      {language === 'nl' ? 'Je like is opgeslagen. Upgrade om contact op te nemen!' : 'Your like is saved. Upgrade to reach out!'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/upgrade-match/${selectedMatch?.listing?.id}`)}
                  className="w-full bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg text-sm"
                >
                  <Crown className="w-4 h-4" />
                  {language === 'nl' ? 'Upgrade nu' : 'Upgrade now'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F7F7F7] flex flex-col overflow-hidden" data-testid="dashboard-page">
      <Toaster />
      
      {/* Header - Clean and minimal */}
      <div className="bg-white border-b border-[#E5E5E5] px-4 py-4 safe-area-top">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="w-10 h-10 bg-[#F7F7F7] rounded-full flex items-center justify-center hover:bg-[#E5E5E5] transition-colors">
              <Home className="w-5 h-5 text-[#2A2A2A]" />
            </button>
            <SwipeMatchLogo size="default" variant="color" />
            <button onClick={() => navigate('/profiel')} className="w-10 h-10 bg-[#F7F7F7] rounded-full flex items-center justify-center hover:bg-[#E5E5E5] transition-colors">
              <User className="w-5 h-5 text-[#2A2A2A]" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* Matches Section */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-[#2A2A2A]">
                {t.matches} 
                {matches.length > 0 && (
                  <span className="ml-2 text-sm font-normal bg-[#2E7D32] text-white px-2 py-0.5 rounded-full">
                    {matches.length}
                  </span>
                )}
              </h2>
            </div>

            {matches.length === 0 ? (
              /* Empty State */
              <div className="bg-white rounded-3xl p-8 text-center border border-[#E5E5E5]">
                <div className="w-20 h-20 bg-[#F7F7F7] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-[#E5E5E5]" />
                </div>
                <h3 className="text-lg font-bold text-[#2A2A2A] mb-2">{t.noMatches}</h3>
                <p className="text-[#9E9E9E] mb-6">{t.noMatchesDesc}</p>
                <Button 
                  onClick={() => navigate('/swipematch/swipe')}
                  className="bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl px-8 h-12"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  {t.startSwiping}
                </Button>
              </div>
            ) : (
              /* Match List - With view listing and chat options */
              <div className="space-y-3">
                {matches.map((match, index) => (
                  <div 
                    key={match.id || index}
                    className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-all"
                    data-testid="match-card"
                  >
                    <div className="flex">
                      {/* Property Image - Clickable to view listing */}
                      <button
                        onClick={() => navigate(`/woning/${match.listing?.id}`)}
                        className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 relative"
                      >
                        {match.listing?.image ? (
                          <img 
                            src={match.listing.image} 
                            alt={match.listing.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#E5E5E5] flex items-center justify-center">
                            <Building2 className="w-8 h-8 text-[#9E9E9E]" />
                          </div>
                        )}
                        {/* Price badge */}
                        <div className="absolute bottom-2 left-2 bg-[#2E7D32] text-white text-xs font-bold px-2 py-1 rounded-full">
                          €{match.listing?.price}
                        </div>
                      </button>

                      {/* Content */}
                      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                        <div>
                          <h3 className="font-bold text-[#2A2A2A] truncate text-sm">
                            {match.listing?.title || 'Woning'}
                          </h3>
                          <div className="flex items-center gap-1 text-[#9E9E9E] text-xs mt-0.5">
                            <MapPin className="w-3 h-3 text-[#2E7D32]" />
                            <span className="truncate">{match.listing?.city || 'Nederland'}</span>
                          </div>
                          {match.landlord?.name && (
                            <div className="flex items-center gap-1 text-[#9E9E9E] text-xs mt-1">
                              <User className="w-3 h-3" />
                              <span className="truncate">{match.landlord.name}</span>
                            </div>
                          )}
                        </div>

                        {/* Action buttons - Chat or Upgrade based on pending status */}
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => navigate(`/woning/${match.listing?.id}`)}
                            className="flex-1 flex items-center justify-center gap-1 h-8 bg-[#F7F7F7] hover:bg-[#E5E5E5] rounded-lg text-xs font-medium text-[#2A2A2A] transition-colors"
                            data-testid="view-listing-btn"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>{language === 'nl' ? 'Bekijk' : 'View'}</span>
                          </button>
                          {/* Show Chat if canChat (premium or paid swipe), otherwise show Upgrade CTA */}
                          {match.canChat !== false ? (
                            <button
                              onClick={() => loadChat(match)}
                              className="flex-1 flex items-center justify-center gap-1 h-8 bg-[#2E7D32] hover:bg-[#256B2B] rounded-lg text-xs font-bold text-white transition-colors"
                              data-testid="open-chat-btn"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              <span>{language === 'nl' ? 'Chat' : 'Chat'}</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate(`/upgrade-match/${match.listing?.id}`)}
                              className="flex-1 flex items-center justify-center gap-1 h-8 bg-[#1F3A5F] hover:bg-[#162D4A] rounded-lg text-xs font-bold text-white transition-colors"
                              data-testid="upgrade-to-chat-btn"
                            >
                              <Lock className="w-3.5 h-3.5" />
                              <span>{language === 'nl' ? 'Upgrade' : 'Upgrade'}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Match indicator - show pending status */}
                    <div className="px-3 pb-2 flex items-center gap-1">
                      <Heart className="w-3 h-3 fill-[#2E7D32] text-[#2E7D32]" />
                      <span className="text-[10px] text-[#2E7D32] font-medium">
                        {match.isPending 
                          ? (language === 'nl' ? 'Geliked - Upgrade om te chatten' : 'Liked - Upgrade to chat')
                          : (language === 'nl' ? 'Match' : 'Match')
                        }
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Premium Upsell - Show benefits of premium */}
          {matches.length > 0 && !hasPremium && (
            <div className="bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] rounded-2xl p-5 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Crown className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold mb-1">{language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swiping'}</h3>
                  <p className="text-sm text-white/80 mb-3">{language === 'nl' ? 'Upgrade voor onbeperkte swipes en een geverifieerde badge' : 'Upgrade for unlimited swipes and a verified badge'}</p>
                  <Button 
                    onClick={handleNavigateToPremium}
                    disabled={navigatingToPremium}
                    className="bg-white text-[#1F3A5F] hover:bg-white/90 h-10 px-6 rounded-xl font-bold text-sm"
                  >
                    {language === 'nl' ? 'Bekijk Premium' : 'View Premium'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-[#E5E5E5] px-4 py-3 safe-area-bottom">
        <div className="max-w-2xl mx-auto flex items-center justify-around">
          <button onClick={() => navigate('/')} className="flex flex-col items-center gap-1 text-[#9E9E9E] hover:text-[#2A2A2A] transition-colors">
            <Home className="w-6 h-6" />
            <span className="text-xs">Home</span>
          </button>
          <button onClick={() => navigate('/swipematch/swipe')} className="flex flex-col items-center gap-1 text-[#9E9E9E] hover:text-[#2A2A2A] transition-colors">
            <Heart className="w-6 h-6" />
            <span className="text-xs">Swipe</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-[#2E7D32]">
            <MessageSquare className="w-6 h-6 fill-[#2E7D32]/20" />
            <span className="text-xs font-medium">{t.matches}</span>
          </button>
          <button onClick={() => navigate('/profiel')} className="flex flex-col items-center gap-1 text-[#9E9E9E] hover:text-[#2A2A2A] transition-colors">
            <User className="w-6 h-6" />
            <span className="text-xs">{t.profile}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
